import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button.tsx";
import { Footer } from "@/components/layout/Footer.tsx";
import { Navbar } from "@/components/layout/Navbar.tsx";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card.tsx";
import { Badge } from "@/components/ui/badge.tsx";
import {
  createNewStory,
  deleteStory,
  getSavedStories,
  type SavedStory,
} from "@/lib/storage/stories.ts";
import { useUserAuth } from "@/hooks/useUserAuth.ts";
import { Calendar, Plus, Settings, Trash2 } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion.tsx";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog.tsx";
import plotCatLogo from "@/assets/plot-cat-logo.gif";

export function Landing() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useUserAuth();
  const [stories, setStories] = useState<SavedStory[]>([]);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [storyToDelete, setStoryToDelete] = useState<string | null>(null);
  const [isLoadingStories, setIsLoadingStories] = useState(true);

  useEffect(() => {
    void loadStories();
  }, [user?.id]);

  const loadStories = async () => {
    setIsLoadingStories(true);
    const saved = await getSavedStories();
    setStories(saved);
    setIsLoadingStories(false);
  };

  const handleCreateNew = async () => {
    const newStory = await createNewStory();
    navigate(`/story/${newStory.id}`);
  };

  const handleDeleteClick = (storyId: string, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setStoryToDelete(storyId);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (storyToDelete) {
      await deleteStory(storyToDelete);
      await loadStories();
      setStoryToDelete(null);
    }
    setDeleteDialogOpen(false);
  };

  const formatDate = (timestamp: number) => {
    return new Date(timestamp).toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <div className="relative flex min-h-screen flex-col bg-background overflow-x-hidden dither-bg">
      <Navbar />

      <main className="flex-1 overflow-x-hidden">
        {/* Hero Section */}
        <section className="relative overflow-hidden pt-12 scanlines">
          <div className="absolute -top-36 left-1/2 h-96 w-[160vw] -translate-x-1/2 -rotate-6 bg-black dark:bg-black/90 pointer-events-none" />
          <div className="relative z-10 mx-auto max-w-6xl px-6 text-center space-y-6">
            <div className="inline-block border-4 border-accent bg-black p-6 brutal-shadow-accent mx-auto">
              <img
                src={plotCatLogo}
                alt="Plot Cat"
                className="w-64 h-64 object-contain"
                style={{ imageRendering: "pixelated" }}
              />
            </div>

            <div className="space-y-1">
              <h1 className="font-pixel text-3xl md:text-5xl tracking-wide uppercase crt-glow">
                <span className="text-accent">
                  {t("landing.hero.titleLine1")}
                </span>
              </h1>
              <h1 className="font-pixel text-xl md:text-3xl tracking-wide uppercase text-foreground">
                {t("landing.hero.titleLine2")}
              </h1>
              <p className="font-pixel text-muted-foreground text-[10px] md:text-xs tracking-widest uppercase pt-2">
                {t("landing.hero.subtitle")}
                <span className="blink ml-1">_</span>
              </p>
            </div>

            <div className="text-center space-y-6 pt-4">
              <p className="text-sm md:text-base text-muted-foreground max-w-2xl mx-auto leading-relaxed">
                {t("landing.hero.description")}
              </p>
              <Button
                variant="brutalist"
                size="brutalist"
                onClick={handleCreateNew}
                className="gap-3"
              >
                <Plus className="h-5 w-5" />
                {t("landing.hero.cta")}
              </Button>
            </div>
          </div>

          {/* Pixel divider */}
          <div className="pixel-divider mt-12" />
        </section>

        {/* Quote Section */}
        <section className="py-16">
          <div className="mx-auto max-w-3xl px-6">
            <div className="relative border-4 border-accent bg-card p-8 md:p-12 brutal-shadow-accent">
              <div className="absolute -top-4 left-6 bg-accent px-3 py-1">
                <span className="font-pixel text-[10px] uppercase tracking-wider text-accent-foreground">
                  Lore
                </span>
              </div>
              <p className="text-lg md:text-xl lg:text-2xl leading-relaxed text-foreground font-medium">
                "{t("landing.quote.text")}"
              </p>
              <p className="mt-6 font-pixel text-accent uppercase tracking-wider text-[10px]">
                -- {t("landing.quote.author")}
                <span className="blink ml-1">_</span>
              </p>
            </div>
          </div>
        </section>

        {/* Pixel divider */}
        <div className="pixel-divider" />

        {/* Saved Stories Section -- full-bleed black band */}
        <section className="border-y-4 border-accent bg-black py-12">
          <div className="mx-auto max-w-6xl px-6 space-y-8">
            <div className="flex items-center justify-between">
              <h2 className="font-pixel text-lg md:text-xl uppercase tracking-wide text-white">
                {t("landing.savedStories")}
              </h2>
              <Button
                onClick={handleCreateNew}
                variant="brutalist"
                size="brutalist"
                className="text-[10px] px-5 py-3"
              >
                <Plus className="mr-1 h-4 w-4" />
                {t("landing.newStory", { defaultValue: "New Story" })}
              </Button>
            </div>

            {isLoadingStories
              ? (
                <Card className="border-2 border-dashed border-accent/30 bg-white/5">
                  <CardContent className="py-12 text-center">
                    <p className="font-pixel text-[10px] text-white/50 mb-6 uppercase tracking-wide">
                      {t("common.loading", { defaultValue: "Loading..." })}
                    </p>
                  </CardContent>
                </Card>
              )
              : stories.length === 0
              ? (
                <Card className="border-2 border-dashed border-accent/30 bg-white/5">
                  <CardContent className="py-12 text-center">
                    <p className="font-pixel text-[10px] text-white/50 mb-6 uppercase tracking-wide">
                      {t("landing.noStories")}
                    </p>
                    <Button
                      onClick={handleCreateNew}
                      variant="outline"
                      className="border-accent/40 text-white hover:bg-accent/10"
                    >
                      <Plus className="mr-2 h-4 w-4" />
                      {t("landing.createFirst", {
                        defaultValue: "Create your first story",
                      })}
                    </Button>
                  </CardContent>
                </Card>
              )
              : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {stories.map((story) => (
                    <Link key={story.id} to={`/story/${story.id}`}>
                      <Card className="h-full cursor-pointer transition-all hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-none border-2 border-accent/40 bg-card shadow-[4px_4px_0px_0px_hsl(var(--accent)/0.3)]">
                        <CardHeader>
                          <CardTitle className="flex items-start justify-between gap-2">
                            <span className="font-pixel text-[10px] uppercase leading-relaxed line-clamp-2 text-accent">
                              {story.storySeed.storyName || story.name}
                            </span>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 shrink-0"
                              onClick={(e) => handleDeleteClick(story.id, e)}
                            >
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          </CardTitle>
                          <CardDescription className="flex items-center gap-2">
                            <Calendar className="h-3 w-3" />
                            {formatDate(story.timestamp)}
                          </CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-3">
                          <div className="flex items-center gap-2 text-sm text-muted-foreground">
                            <Settings className="h-3 w-3" />
                            <span>
                              {story.settings.theme ||
                                t("editor.themeNone", {
                                  defaultValue: "None",
                                })} • {story.settings.ageGroup} • {story.settings.language}
                            </span>
                          </div>

                          <div className="flex flex-wrap gap-1">
                            {story.lockState.mainObjective && (
                              <Badge variant="secondary" className="text-[10px]">
                                {t("story.mainObjective")}
                              </Badge>
                            )}
                            {story.lockState.characters.filter(Boolean).length >
                                0 && (
                              <Badge variant="secondary" className="text-[10px]">
                                {story.lockState.characters.filter(Boolean)
                                  .length} {t("story.characters")}
                              </Badge>
                            )}
                            {story.lockState.locations.filter(Boolean).length >
                                0 && (
                              <Badge variant="secondary" className="text-[10px]">
                                {story.lockState.locations.filter(Boolean).length}{" "}
                                {t("story.locations")}
                              </Badge>
                            )}
                            {story.lockState.skills.filter(Boolean).length >
                                0 && (
                              <Badge variant="secondary" className="text-[10px]">
                                {story.lockState.skills.filter(Boolean).length} {t("story.skills")}
                              </Badge>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    </Link>
                  ))}
                </div>
              )}
          </div>
        </section>

        <div className="mx-auto max-w-6xl space-y-16 px-6 pb-16 pt-8">
          {/* Pixel divider */}
          <div className="pixel-divider" />

          {/* FAQ Section */}
          <section className="space-y-4 pt-4">
            <h2 className="font-pixel text-lg md:text-xl uppercase tracking-wide text-center">
              {t("landing.faq.title")}
            </h2>
            <Card className="border-2 brutal-shadow-lg">
              <CardContent className="pt-6">
                <Accordion type="single" collapsible className="w-full">
                  <AccordionItem
                    value="what-is-plot-cat"
                    className="border-b-2"
                  >
                    <AccordionTrigger className="font-bold hover:no-underline uppercase tracking-wide text-sm">
                      {t("landing.faq.q1")}
                    </AccordionTrigger>
                    <AccordionContent className="text-muted-foreground">
                      {t("landing.faq.a1")}
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem
                    value="what-is-amazing-tales"
                    className="border-b-2"
                  >
                    <AccordionTrigger className="font-bold hover:no-underline uppercase tracking-wide text-sm">
                      {t("landing.faq.q2")}
                    </AccordionTrigger>
                    <AccordionContent className="text-muted-foreground">
                      {t("landing.faq.a2")}
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem
                    value="how-does-it-work"
                    className="border-b-2"
                  >
                    <AccordionTrigger className="font-bold hover:no-underline uppercase tracking-wide text-sm">
                      {t("landing.faq.q3")}
                    </AccordionTrigger>
                    <AccordionContent className="text-muted-foreground">
                      {t("landing.faq.a3")}
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="story-elements" className="border-b-2">
                    <AccordionTrigger className="font-bold hover:no-underline uppercase tracking-wide text-sm">
                      {t("landing.faq.q4")}
                    </AccordionTrigger>
                    <AccordionContent className="text-muted-foreground">
                      {t("landing.faq.a4")}
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="where-to-buy">
                    <AccordionTrigger className="font-bold hover:no-underline uppercase tracking-wide text-sm">
                      {t("landing.faq.q5")}
                    </AccordionTrigger>
                    <AccordionContent className="text-muted-foreground">
                      {t("landing.faq.a5Prefix")}{"  "}
                      <a
                        href="https://www.drivethrurpg.com/en/product/222950/amazing-tales-complete-kids-rpg?src=plot-cat"
                        target="_blank"
                        rel="noreferrer"
                        className="text-accent underline underline-offset-2"
                      >
                        {t("landing.faq.a5LinkText")}
                      </a>
                      . {t("landing.faq.a5Middle")} {t("landing.faq.a5Suffix")}
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              </CardContent>
            </Card>
          </section>
        </div>
      </main>

      <Footer />

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="font-pixel text-sm uppercase">
              {t("landing.deleteConfirmTitle", {
                defaultValue: "Delete Story?",
              })}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t("landing.deleteConfirmMessage", {
                defaultValue:
                  "This action cannot be undone. The story will be permanently deleted.",
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>
              {t("common.cancel", { defaultValue: "Cancel" })}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              className="bg-destructive text-destructive-foreground"
            >
              {t("common.delete", { defaultValue: "Delete" })}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
